import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Paper,
  Card,
  Button,
  CircularProgress,
  TextField,
  useMediaQuery,
  InputAdornment,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { debounce } from "lodash"; // Import lodash for debouncing
import apis from "../utils/apis";
import Header from "../components/Header";
import ReactGA from "react-ga4";
import { getEmail, saveTargetPath } from "../utils/storage";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import CollapsibleTable from "../components/common/CollapsibleTable";

// Styled components for table
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: "#FDF9E0",
  borderColor: "#FDF9E0",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(39, 140, 55, 0.1)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "transparent",
  },
}));

const formatISTDateTime = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const ResumeHistory = () => {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0); // New state for total count
  const [loading, setLoading] = useState(true); // Full-page loader
  const [error, setError] = useState(null); // Full-page error
  const [tableLoading, setTableLoading] = useState(false); // Table-specific loader
  const [tableError, setTableError] = useState(null); // Table-specific error
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5); // Default value set to 5
  const [searchQuery, setSearchQuery] = useState(""); // Added state for search query
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate(); // Initialize useNavigate

  // Debounced search handler
  const handleSearchChange = debounce((event) => {
    setSearchQuery(event.target.value.toLowerCase());
  }, 300);

  const cache = useRef({}); // Cache to store data for each page

  // Fetch data from API
  const fetchData = async (currentPage, limit, isTableLoading = false) => {
    if (cache.current[currentPage]) {
      // If data for the page is cached, use it
      setData(cache.current[currentPage].data);
      setTotalCount(cache.current[currentPage].totalCount);
      return;
    }

    if (isTableLoading) setTableLoading(true);
    else setLoading(true);

    try {
      const response = await apis.GET({
        endpoint: `/get-resume-history?page=${currentPage}&limit=${limit}`,
      });
      if (response.status === 201) {
        const pageData = response.data.data || [];
        const totalCount = response.data.totalCount || 0;

        // Cache the data for the current page
        cache.current[currentPage] = { data: pageData, totalCount };

        setData(pageData);
        setTotalCount(totalCount);
      } else {
        throw new Error("Failed to fetch data");
      }
    } catch (err) {
      if (isTableLoading) setTableError("Failed to load table data.");
      else setError("Failed to load data. Please try again later.");
    } finally {
      if (isTableLoading) setTableLoading(false);
      else setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData(1, rowsPerPage); // Initial load only
  }, []); // Empty dependency array for initial load

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData(newPage + 1, rowsPerPage, true); // Fetch data for the new page
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    cache.current = {}; // Clear cache when rows per page changes
    fetchData(1, newRowsPerPage, true);
  };

  // Utility function to convert months into a human-readable format
  const formatExperience = (totalMonths) => {
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    let experienceText = "";
    if (years > 0) {
      experienceText += `${years} ${years > 1 ? "Years" : "Year"}`;
    }
    if (months > 0) {
      experienceText += ` ${months} ${months > 1 ? "Months" : "Month"}`;
    }
    if (totalMonths === 0) {
      return "Fresher/Intern/Trainee";
    }
    return experienceText.trim();
  };

  const filteredData = Array.isArray(data)
    ? data.filter((row) => {
        const query = searchQuery.toLowerCase();

        // Priority-based conditions
        if (query === "peer reviewed") {
          return row.evaluationTested;
        }
        if (query === "self attested") {
          return row.selfAttested && !row.evaluationTested && !row.resumeParsed;
        }
        if (query === "resume based") {
          return row.resumeParsed && !row.evaluationTested && !row.selfAttested;
        }

        // General search conditions
        return (
          row.name?.toLowerCase().includes(query) ||
          formatISTDateTime(row.time).toLowerCase().includes(query) ||
          formatExperience(parseInt(row.userform?.experience || 0, 10))
            .toLowerCase()
            .includes(query) ||
          row.userform?.location?.toLowerCase().includes(query) ||
          row.userform?.currentCompany?.toLowerCase().includes(query) ||
          row.userform?.role?.toLowerCase().includes(query) ||
          row.userform?.email?.toLowerCase().includes(query) ||
          row.userform?.mobile?.toLowerCase().includes(query)
        );
      })
    : [];

  // Handle upload resume button click
  const handleUploadResumeClick = () => {
    saveTargetPath("/Upload-Resume");
    ReactGA.event({
      category: "Button",
      action: "Click",
      label: "Upload Your Resume",
    });
  };

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        minHeight: "100vh",
        width: "100%",
        color: "#FDF9E0",
        backgroundImage: "url('../../assets/images/homepage-background.svg')",
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: isMobile ? 2 : 4,
      }}
    >
      <Header />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress size={50} />
          <Typography sx={{ mt: 2, textAlign: "center" }}>
            Loading...
          </Typography>
        </Box>
      ) : error ? (
        <Typography sx={{ textAlign: "center", color: "red" }}>
          {error}
        </Typography>
      ) : Array.isArray(data) && data.length > 0 ? (
        <Box>
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              mt: { xs: 10, md: 3 },
              mb: { xs: 2, md: 2 },
              fontWeight: 700,
              fontFamily: "Kanit",
            }}
          >
            View History
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <TextField
              variant="outlined"
              placeholder="Search by Name, Date, Years of Experience, Location, Last Company, Last Role"
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#26801E" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: isMobile ? "100%" : "50%",
                borderRadius: "5px",
                borderColor: "primary",
                "& .MuiInputBase-root": {
                  backgroundColor: "#FDF9E0",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#FDF9E0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#26801E",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#26801E",
                  },
                },
              }}
            />
          </Box>
          <Paper
            sx={{
              backgroundColor: "transparent",
              display: "flex",
              flexDirection: "column", // Ensure the pagination is below the table
              justifyContent: "center",
              alignItems: "center",
              padding: isMobile ? 1 : 3,
            }}
          >
            <TableContainer
              sx={{
                border: "1px solid #FDF9E0",
                borderRadius: "8px",
                maxWidth: {
                  xs: "100%",
                  md: "100%",
                  xl: "90%",
                },
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  margin: "2px",
                  background: "#231F20",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#FDF9E0",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#26801E",
                },
              }}
            >
              {isMobile ? (
                // Mobile View: Render as cards
                <Box>
                  {tableLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100px", // Adjust height as needed
                      }}
                    >
                      <CircularProgress size={30} sx={{ color: "#FDF9E0" }} />
                    </Box>
                  ) : (
                    filteredData.map((row, index) => (
                      <React.Fragment key={row.id || index}>
                        <Paper
                          sx={{
                            padding: 2,
                            backgroundColor: "rgba(39, 140, 55, 0.1)",
                            borderRadius: "8px",
                          }}
                        >
                          <Typography sx={{ color: "#FDF9E0" }} variant="body1">
                            <strong>No:</strong>{" "}
                            {page * rowsPerPage + index + 1}
                          </Typography>
                          <Typography sx={{ color: "#FDF9E0" }} variant="body1">
                            <strong>Date & Time:</strong>{" "}
                            {formatISTDateTime(row.time)}
                          </Typography>
                          <Typography sx={{ color: "#FDF9E0" }} variant="body1">
                            <strong>Name:</strong> {row.name || "N/A"}
                          </Typography>
                          <Typography sx={{ color: "#FDF9E0" }} variant="body1">
                            <strong>Status:</strong>
                          </Typography>
                          {row.evaluationTested ? (
                            <img
                              src="/assets/images/peerreviewed.svg"
                              alt="Peer Reviewed"
                              style={{
                                width: "80px",
                                height: "80px",
                                borderRadius: "8px",
                              }}
                            />
                          ) : row.selfAttested ? (
                            <img
                              src="/assets/images/SELF-ATTESTED.svg"
                              alt="Self Attested"
                              style={{
                                width: "80px",
                                height: "80px",
                                borderRadius: "8px",
                              }}
                            />
                          ) : row.resumeParsed ? (
                            <img
                              src="/assets/images/ResumeBased.svg"
                              alt="Resume Based"
                              style={{
                                width: "80px",
                                height: "80px",
                                borderRadius: "8px",
                              }}
                            />
                          ) : (
                            <Typography
                              sx={{ color: "#FDF9E0" }}
                              variant="body1"
                            >
                              N/A
                            </Typography>
                          )}
                          <Typography sx={{ color: "#FDF9E0" }} variant="body1">
                            <strong>Years of Experience:</strong>{" "}
                            {isNaN(row.userform?.experience)
                              ? `Total experience of ${row.userform?.experience}`
                              : formatExperience(
                                  parseInt(row.userform?.experience, 10)
                                )}
                          </Typography>
                          <Typography sx={{ color: "#FDF9E0" }} variant="body1">
                            <strong>Location:</strong>{" "}
                            {row.userform?.location || "N/A"}
                          </Typography>
                          <Typography sx={{ color: "#FDF9E0" }} variant="body1">
                            <strong>Last Company:</strong>{" "}
                            {row.userform?.currentCompany || "N/A"}
                          </Typography>
                          <Typography sx={{ color: "#FDF9E0" }} variant="body1">
                            <strong>Last Role:</strong>{" "}
                            {row.userform?.role || "N/A"}
                          </Typography>
                          <Box
                            sx={{
                              marginTop: 1,
                              display: "flex",
                              justifyContent: "center",
                              gap: 1,
                            }}
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              href={row.fileUrl}
                              target="_blank"
                              sx={{
                                color: "#FDF9E0",
                                borderRadius: "8px",
                                textTransform: "none",
                                textAlign: "center",
                              }}
                            >
                              View Resume
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              href={`/resume-history/${row.uuid}`}
                              target="_blank"
                              sx={{
                                color: "#FDF9E0",
                                borderRadius: "8px",
                                textAlign: "center",
                                textTransform: "none",
                              }}
                            >
                              Public Profile
                            </Button>
                          </Box>
                        </Paper>
                        {index < filteredData.length - 1 && (
                          <Box
                            sx={{
                              height: "1px",
                              backgroundColor: "#FDF9E0",
                              margin: "8px 0",
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))
                  )}
                </Box>
              ) : (
                // Desktop View: Render as table
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>No</StyledTableCell>
                      <StyledTableCell>Date & Time</StyledTableCell>
                      <StyledTableCell>Name</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                      <StyledTableCell>Years of Experience</StyledTableCell>
                      <StyledTableCell>Location</StyledTableCell>
                      <StyledTableCell>Last Company</StyledTableCell>
                      <StyledTableCell>Last Role</StyledTableCell>
                      <StyledTableCell>Action</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableLoading ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <CircularProgress
                            size={30}
                            sx={{ color: "#FDF9E0" }}
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredData.map((row, index) => (
                        <StyledTableRow key={row.id || index}>
                          <StyledTableCell>
                            {page * rowsPerPage + index + 1}
                          </StyledTableCell>
                          <StyledTableCell>
                            {formatISTDateTime(row.time)}
                          </StyledTableCell>
                          <StyledTableCell>{row.name || "N/A"}</StyledTableCell>
                          <StyledTableCell>
                            {row.evaluationTested ? (
                              <img
                                src="/assets/images/peerreviewed.svg"
                                alt="Peer Reviewed"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  borderRadius: "8px",
                                }}
                              />
                            ) : row.selfAttested ? (
                              <img
                                src="/assets/images/SELF-ATTESTED.svg"
                                alt="Self Attested"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  borderRadius: "8px",
                                }}
                              />
                            ) : row.resumeParsed ? (
                              <img
                                src="/assets/images/ResumeBased.svg"
                                alt="Resume Based"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  borderRadius: "8px",
                                }}
                              />
                            ) : (
                              "N/A"
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {isNaN(row.userform?.experience)
                              ? `Total experience of ${row.userform?.experience}`
                              : formatExperience(
                                  parseInt(row.userform?.experience, 10)
                                )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.userform?.location || "N/A"}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.userform?.currentCompany || "N/A"}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.userform?.role || "N/A"}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                variant="contained"
                                color="primary"
                                href={row.fileUrl}
                                target="_blank"
                                sx={{
                                  color: "#FDF9E0",
                                  borderRadius: "8px",
                                  textTransform: "none",
                                  textAlign: "center",
                                }}
                              >
                                View Resume
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                href={`/resume-history/${row.uuid}`}
                                target="_blank"
                                sx={{
                                  color: "#FDF9E0",
                                  borderRadius: "8px",
                                  textAlign: "center",
                                  textTransform: "none",
                                }}
                              >
                                Public Profile
                              </Button>
                            </Box>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
            <Box
              sx={{
                width: "100%",
                maxWidth: {
                  xs: "100%",
                  md: "100%",
                  xl: "90%",
                },
                // position: "sticky", // Make the pagination sticky
                bottom: 0, // Stick to the bottom of the container
                backgroundColor: "transparent", // Match the background color
                zIndex: 1, // Ensure it stays above the table content
              }}
            >
              <TablePagination
                component="div"
                count={totalCount} // Use totalCount from API
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{
                  "&::-webkit-scrollbar": {
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#231F20",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#FDF9E0",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#26801E",
                  },
                  color: "#FDF9E0",
                  "& .MuiTablePagination-root": {
                    color: "#FDF9E0",
                  },
                  "& .MuiTablePagination-select": {
                    color: "#FDF9E0",
                  },
                  "& .MuiTablePagination-actions button": {
                    color: "#FDF9E0",
                  },
                  "& .MuiInputBase-root-MuiTablePagination-select": {
                    mr: "15px",
                  },
                  "& svg": {
                    fill: "#FDF9E0 !important",
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>
      ) : (
        <Card
          sx={{
            backgroundColor: "#1D3A24",
            borderRadius: "16px",
            padding: { xs: 3, sm: 5, md: 6 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#FDF9E0",
            textAlign: "center",
            position: "relative",
            maxWidth: "500px",
            margin: "auto",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              marginBottom: { xs: "0px", sm: 1 },
              fontSize: { xs: "18px", sm: "22px", md: "24px" },
              fontWeight: 500,
            }}
          >
            No data found.
          </Typography>
          <Typography
            variant="h6"
            sx={{
              marginBottom: { xs: 2, sm: 3 },
              fontSize: { xs: "18px", sm: "22px", md: "24px" },
              fontWeight: 500,
            }}
          >
            Please upload your resume to get started.
          </Typography>
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#278C37",
                color: "#FDF9E0",
                borderRadius: { xs: "6px", sm: "8px", md: "10px" },
                padding: { xs: "8px 14px", sm: "10px 20px", md: "10px 30px" },
                fontSize: { xs: "14px", sm: "18px", md: "24px" },
                fontWeight: { xs: 500, md: 700 },
                textTransform: "none",
              }}
              href="/Upload-Resume"
              onClick={handleUploadResumeClick}
            >
              Upload Your Resume
            </Button>
            <Box
              component="img"
              src="../../assets/images/click-indicator.svg"
              alt="Indicator Icon"
              sx={{
                height: "100%",
                width: "100%",
                position: "absolute",
                bottom: { xs: "-7px", sm: "-10px", md: "-15px" },
                right: { xs: "-4px", sm: "-10px", md: "-17px" },
                maxHeight: { xs: "18px", sm: "30px", md: "44px" },
                maxWidth: { xs: "18px", sm: "30px", md: "44px" },
              }}
            />
          </Box>
        </Card>
      )}
      <Box
        sx={{ display: "flex", justifyContent: "center", mt: { xs: 2, md: 0 } }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/Upload-Resume")} // Redirect to the upload route
          sx={{
            color: "#FDF9E0",
            textTransform: "none",
            borderRadius: "8px",
            padding: "10px 20px",
            "&:hover": {
              backgroundColor: "#1E6A16",
            },
          }}
        >
          Back to Upload
        </Button>
      </Box>
    </Box>
  );
};

export default ResumeHistory;
